import { createRouter, createWebHistory, parseQuery, START_LOCATION } from 'vue-router';
import { useLoadingStore } from './stores/loading-store';
import { useAppStateStore } from './stores/app-state-store';
import { useCoursesStore } from './stores/courses-store';
import { useEventsListStore } from './stores/events-list-store';
import GutenbergPage from '@/js/core/components/pages/GutenbergPage.vue';
import TraysPage from '@/js/core/components/pages/TraysPage.vue';
import CalendarPage from '@/js/core/components/pages/CalendarPage.vue';
import StartNewTrayModal from '@/js/core/components/pages/StartNewTrayModal.vue';
import ViewTrayModal from '@/js/core/components/pages/ViewTrayModal.vue';
import ViewEventModal from '@/js/core/components/pages/ViewEventModal.vue';
import AcademicCalendarPage from '@/js/core/components/pages/AcademicCalendarPage.vue';
import ProfilePage from '@/js/core/components/pages/ProfilePage.vue';
import CoursesPage from '@/js/core/components/pages/CoursesPage.vue';
import ViewCourseModal from '@/js/core/components/pages/ViewCourseModal.vue';
import { FEATURES } from '@/js/core/constants/features';
import { canAccessFeature } from '@/js/core/utils/feature-access';
import { canAccessFullAcademicYear } from '@/js/core/utils/full-academic-year-access';
import { setSpaModalOrigin } from '@/js/core/utils/modal-origin';
import { useTrayViewStore } from './stores/tray-view-store';
import { trayUuidFromRouteSlug } from '@/js/core/utils/tray-route';
import { applyPendingReturnScroll } from '@/js/core/utils/return-scroll';
import { ownsUrl } from '@/js/core/utils/modal-route';
import { coursesQueryFromFilters } from '@/js/core/utils/courses';
import { parseCoursesState } from '@/js/core/utils/courses-url-state';
import DigitalDisplayEventsPage from '@/js/core/components/pages/DigitalDisplayEventsPage.vue';

// The event modal opens the same route from multiple locations so we capture the
// origin and navigate back there when the modal is closed
const EVENT_MODAL_ORIGIN_ROUTES = ['calendar', 'academic-calendar'];
let eventModalOrigin = null;

// Same shape for the course modal so we can return to pre-filtered state on modal close
let courseModalOrigin = null;

function trackEventModalOrigin(to, from) {
  if (to.name !== 'event-view' || from.name === 'event-view') return;

  eventModalOrigin = EVENT_MODAL_ORIGIN_ROUTES.includes(from.name)
    ? { name: from.name, query: originQuery(from) }
    : null;
}

function trackCourseModalOrigin(to, from) {
  if (to.name !== 'course-view' || from.name === 'course-view') return;

  courseModalOrigin =
    from.name === 'courses' ? { name: 'courses', query: originQuery(from) } : null;
}

function originQuery(from) {
  const originPath = from.path.replace(/\/+$/, '');
  const livePath = window.location.pathname.replace(/\/+$/, '');

  return originPath === livePath ? parseQuery(window.location.search) : { ...from.query };
}

// Record the page a modal was opened from, so closing it can restore that page
// through the router instead
function trackSpaModalOrigin(to, from) {
  if (ownsUrl(to)) {
    setSpaModalOrigin(null);
    return;
  }

  // Modal to modal keeps the page that opened the first one
  if (!ownsUrl(from)) return;

  // START_LOCATION means the modal route was the document's own entry point, so
  // there is no earlier page held in memory to go back to
  setSpaModalOrigin(from === START_LOCATION ? null : { path: from.path, query: { ...from.query } });
}

// The GA4 tag is loaded with send_page_view off (app/Config/Analytics.php), so
// every view is sent from here. window.gtag only exists where the tag rendered (ie every page in Prod)
function trackPageView() {
  window.gtag?.('event', 'page_view', {
    page_location: window.location.href,
    page_title: document.title,
  });
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      name: 'home',
      path: '/',
      components: {
        default: GutenbergPage,
      },
    },

    {
      name: 'calendar',
      path: '/calendar',
      meta: {
        requiredFeature: FEATURES.calendarEvents,
      },
      components: {
        default: CalendarPage,
      },
    },

    {
      name: 'digital-display-events',
      path: '/calendar/digital-display/key/:key',
      components: {
        default: DigitalDisplayEventsPage,
      },
    },

    {
      name: 'academic-calendar',
      path: '/calendar/academic-calendar',
      meta: {
        navSection: 'calendar',
        requiredFeature: FEATURES.calendarEvents,
      },
      components: {
        default: AcademicCalendarPage,
      },
      beforeEnter: () => {
        // Add an extra class to the container to allow styling full width
        // See also `AcademicCalendarPage.vue` for the part which removes the class on leave
        document.querySelector('#content-container').classList.add('full-page');
      },
    },

    {
      name: 'livestream-instructions',
      path: '/calendar/live-streaming-instructions',
      meta: {
        navSection: 'calendar',
        requiredFeature: FEATURES.calendarEvents,
      },
      components: {
        default: GutenbergPage,
      },
    },

    {
      name: 'event-view',
      path: '/calendar/:eventPageSlug',
      meta: {
        navSection: 'calendar',
        modalCloseTo: route => eventModalOrigin || { name: 'calendar', query: route.query },
        requiredFeature: FEATURES.calendarEvents,
      },
      components: {
        default: CalendarPage,
        modal: ViewEventModal,
      },
    },

    {
      name: 'trays',
      path: '/trays',
      meta: {
        requiredFeature: FEATURES.trays,
      },
      components: {
        default: TraysPage,
      },
    },

    {
      name: 'trays-new',
      path: '/trays/new',
      meta: {
        modalCloseTo: { name: 'trays' },
        requiredFeature: FEATURES.trays,
      },
      components: {
        default: TraysPage,
        modal: StartNewTrayModal,
      },
    },

    {
      name: 'tray-view',
      path: '/trays/:traySlug',
      meta: {
        modalCloseTo: { name: 'trays' },
        requiredFeature: FEATURES.trays,
      },
      components: {
        default: TraysPage,
        modal: ViewTrayModal,
      },
    },

    {
      name: 'faq',
      path: '/faq',
      components: {
        default: GutenbergPage,
      },
    },

    {
      name: 'courses',
      path: '/courses',
      meta: {
        requiredFeature: FEATURES.courses,
      },
      components: {
        default: CoursesPage,
      },
    },

    {
      name: 'course-view',
      path: '/courses/:courseSlug',
      meta: {
        navSection: 'courses',
        modalCloseTo: route => courseModalOrigin || { name: 'courses', query: route.query },
        requiredFeature: FEATURES.courses,
      },
      components: {
        default: CoursesPage,
        modal: ViewCourseModal,
      },
    },

    {
      name: 'profile',
      path: '/profile',
      meta: {
        theme: 'green',
        guestBlocked: true,
      },
      components: {
        default: ProfilePage,
      },
    },

    // Catch-all for editor-created content pages. Must stay last so every
    // explicit route above matches first. PHP has already resolved and
    // hydrated any published WP page (or returned a 404 before the SPA boots).
    {
      name: 'content-page',
      path: '/:pathMatch(.*)*',
      components: {
        default: GutenbergPage,
      },
    },
  ],
});

function routeUsesUpcomingCourses(to) {
  const viewOption = Array.isArray(to.query.view_option)
    ? to.query.view_option[0]
    : to.query.view_option;

  return viewOption === 'calendar';
}

async function preloadCoursesRoute(to, from) {
  if (to.name !== 'courses' && to.name !== 'course-view') return;

  const coursesStore = useCoursesStore();
  const requests = [];

  const indexAlreadyLive =
    to.name === 'course-view' && (from.name === 'courses' || from.name === 'course-view');

  if (!indexAlreadyLive) {
    const urlState = parseCoursesState(to.fullPath.split('?')[1] || '');

    requests.push(
      coursesStore.preloadCoursesPage({
        includeUpcoming: routeUsesUpcomingCourses(to),
        query: coursesQueryFromFilters(urlState.filters),
      })
    );
  }

  if (to.name === 'course-view') {
    requests.push(coursesStore.preloadCourseDetail(to.params.courseSlug));
  }

  await Promise.all(requests);
}

/**
 * Land the tray modal with its data already in the store
 */
async function preloadTrayRoute(to) {
  if (to.name !== 'tray-view') return;

  await useTrayViewStore().ensureTray(trayUuidFromRouteSlug(to.params.traySlug));
}

async function preloadEventRoute(to) {
  if (to.name !== 'event-view') return;

  const eventPageSlug = String(to.params.eventPageSlug || '');
  if (!eventPageSlug) return;

  const eventsStore = useEventsListStore();
  await eventsStore.fetchEvent(eventPageSlug);
}

// Show the progress bar during navigation and enforce feature access
router.beforeEach(async (to, from, next) => {
  const loadingStore = useLoadingStore();
  const appStateStore = useAppStateStore();

  trackEventModalOrigin(to, from);
  trackCourseModalOrigin(to, from);
  trackSpaModalOrigin(to, from);

  loadingStore.start();
  appStateStore.currentPage = to.meta.navSection || to.name;

  const requiredFeature = to.meta.requiredFeature;

  // Redirect to the homepage if the user cannot access feature
  // Uses window data because Pinia may not be hydrated on initial navigation
  if (requiredFeature && !canAccessFeature(window.gsdNowAppData?.userData, requiredFeature)) {
    loadingStore.complete();
    return next('/');
  }

  if (to.meta.guestBlocked && window.gsdNowAppData?.userData?.isGuest) {
    loadingStore.complete();
    return next('/');
  }

  if (to.name === 'academic-calendar' && !canAccessFullAcademicYear()) {
    loadingStore.complete();
    return next({ name: 'calendar' });
  }

  try {
    await preloadCoursesRoute(to, from);
  } catch (err) {
    console.error('Course route preload failed.', err);
  }

  try {
    await preloadEventRoute(to);
  } catch (err) {
    console.error('Event route preload failed.', err);
  }

  // A failure here is not fatal: the modal re-reads and surfaces the real error,
  // including the not-found redirect.
  try {
    await preloadTrayRoute(to);
  } catch (err) {
    console.error('Tray route preload failed.', err);
  }

  next();
});

router.afterEach(to => {
  applyPendingReturnScroll(to);

  trackPageView();

  const loadingStore = useLoadingStore();
  // Small delay to show the progress bar
  setTimeout(() => loadingStore.complete(), 100);
});

export default router;
